import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from 'react-daisyui';
import { FaInfoCircle } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { Package, Clock, FileText } from 'react-feather';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import Sidebar from './Sidebar';
import { validatorData } from './validatorData';
import TopNavbar from './TopNavbar';

const RPC_ENDPOINT = 'https://berachain-v2-testnet-rpc.blacknodes.net';


const StatCard = ({ name, value, icon: Icon }) => (
  <Card className="bg-gray-900 border border-gray-800 ">
    <Card.Body>
      <div className="flex items-center mb-2">
        <Icon className="text-cyan-400 mr-2" size={24} />
        <Card.Title tag="h2">{name}</Card.Title>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </Card.Body>
  </Card>
);

const BlockchainExplorerBlocksPage = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [latestHeight, setLatestHeight] = useState(null);
  const [stats, setStats] = useState({
    totalBlocks: 0,
    avgBlockTime: 0,
    activeValidators: 0,
  });
  const [blockTimes, setBlockTimes] = useState([]);
  const [newBlocks, setNewBlocks] = useState([]);
  const observer = useRef();

  const initialBlockCount = 20;
  const maxBlocksToShow = 100;

  const fetchBlocks = useCallback(async (start, end) => {
    try {
      const blocksResponse = await axios.get(`${RPC_ENDPOINT}/blockchain`, {
        params: {
          minHeight: end,
          maxHeight: start
        }
      });

      return blocksResponse.data.result.block_metas.map(block => ({
        height: block.header.height,
        hash: block.block_id.hash.slice(0, 10) + '...',
        proposer: block.header.proposer_address,
        txCount: block.num_txs,
        timestamp: new Date(block.header.time).toLocaleString()
      }));
    } catch (err) {
      console.error('Error fetching blocks:', err);
      throw err;
    }
  }, []);

  const loadMoreBlocks = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const lastBlockHeight = blocks.length > 0 ? blocks[blocks.length - 1].height - 1 : latestHeight;
      const endHeight = Math.max(lastBlockHeight - initialBlockCount + 1, 1);
      const newBlocks = await fetchBlocks(lastBlockHeight, endHeight);
      setBlocks(prevBlocks => [...prevBlocks, ...newBlocks]);
      setHasMore(endHeight > 1);
    } catch (err) {
      setError('Failed to load more blocks. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [blocks, latestHeight, initialBlockCount, fetchBlocks, hasMore, loading]);

  const lastBlockElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreBlocks();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadMoreBlocks]);

  useEffect(() => {
    const initBlocks = async () => {
      try {
        const statusResponse = await axios.get(`${RPC_ENDPOINT}/status`);
        const newLatestHeight = parseInt(statusResponse.data.result.sync_info.latest_block_height);
        setLatestHeight(newLatestHeight);
        const endHeight = Math.max(newLatestHeight - initialBlockCount + 1, 1);
        const initialBlocks = await fetchBlocks(newLatestHeight, endHeight);
        setBlocks(initialBlocks);
        setHasMore(endHeight > 1);

        // Calculate stats
        const avgBlockTime = (new Date(initialBlocks[0].timestamp) - new Date(initialBlocks[initialBlocks.length - 1].timestamp)) / (initialBlocks.length * 1000);
        setStats({
          totalBlocks: newLatestHeight,
          avgBlockTime: avgBlockTime.toFixed(2),
          activeValidators: Object.keys(validatorData.validators).length,
        });

        // Calculate block times for the chart
        const times = initialBlocks.map((block, index) => ({
          height: block.height,
          time: index === 0 ? avgBlockTime : (new Date(initialBlocks[index - 1].timestamp) - new Date(block.timestamp)) / 1000,
        }));
        setBlockTimes(times.reverse());
      } catch (err) {
        setError('Failed to load initial blocks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    initBlocks();

    const intervalId = setInterval(async () => {
      try {
        const statusResponse = await axios.get(`${RPC_ENDPOINT}/status`);
        const newLatestHeight = parseInt(statusResponse.data.result.sync_info.latest_block_height);
        if (newLatestHeight > latestHeight) {
          const newBlocks = await fetchBlocks(newLatestHeight, latestHeight + 1);
          setBlocks(prevBlocks => {
            const updatedBlocks = [...newBlocks, ...prevBlocks].slice(0, maxBlocksToShow);
            setNewBlocks(newBlocks.map(block => block.height));
            setTimeout(() => setNewBlocks([]), 5000); // Clear highlight after 5 seconds
            return updatedBlocks;
          });
          setLatestHeight(newLatestHeight);

          // Update block times for the chart
          setBlockTimes(prevTimes => {
            const newTimes = newBlocks.map((block, index) => ({
              height: block.height,
              time: index === 0 ? prevTimes[0].time : (new Date(newBlocks[index - 1].timestamp) - new Date(block.timestamp)) / 1000,
            }));
            return [...newTimes, ...prevTimes].slice(0, 20);
          });
        }
      } catch (err) {
        console.error('Error refreshing blocks:', err);
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [fetchBlocks, initialBlockCount, latestHeight, maxBlocksToShow]);

  const columnInfo = {
    height: "The block height in the blockchain",
    hash: "Unique identifier for the block",
    proposer: "Validator that proposed this block",
    txs: "Number of transactions included in this block",
    time: "Timestamp when the block was created"
  };

  const getValidatorInfo = useCallback((address) => {
    const validator = validatorData.validators[address];
    return validator ? {
      name: validator.name,
      logo: `https://raw.githubusercontent.com/blacknodes/logos/main/${validator.name.toLowerCase().replace(/\s+/g, '')}.png`
    } : null;
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar />
      <div className="flex-1 p-4 pl-20">
        <TopNavbar />
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Latest Blocks</h1>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard name="Total Blocks" value={stats.totalBlocks.toLocaleString()} icon={Package} />
            <StatCard name="Avg Block Time" value={`${stats.avgBlockTime}s`} icon={Clock} />
            <StatCard name="Active Validators" value={stats.activeValidators} icon={FileText} />
          </div>

          <Card className="mb-6 bg-gray-900 border border-gray-800 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
            <Card.Body>
              <Card.Title>Block Time Trend</Card.Title>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={blockTimes}>
                  <XAxis dataKey="height" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="time" stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          {error ? (
            <div className="text-red-500">{error}</div>
          ) : (
            <Card className="bg-gray-800 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
              <Card.Body>
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-700">
                      {Object.entries(columnInfo).map(([key, info]) => (
                        <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                          <FaInfoCircle 
                            className="inline-block ml-2 text-gray-500 hover:text-gray-300 cursor-pointer" 
                            data-tooltip-id={`tooltip-${key}`}
                            data-tooltip-content={info}
                          />
                          <Tooltip id={`tooltip-${key}`} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {blocks.map((block, index) => {
                      const validatorInfo = getValidatorInfo(block.proposer);
                      return (
                        <tr 
                          key={block.height} 
                          className={`hover:bg-gray-700 transition-colors duration-200 ${newBlocks.includes(block.height) ? 'bg-blue-900' : ''}`}
                          ref={index === blocks.length - 1 ? lastBlockElementRef : null}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/block/${block.height}`} className="text-blue-400 hover:text-blue-300">
                              {block.height}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{block.hash}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/validator/${block.proposer}`} className="text-blue-400 hover:text-blue-300 flex items-center">
                              {validatorInfo && (
                                <img 
                                  src={validatorInfo.logo}
                                  alt={`${validatorInfo.name} logo`} 
                                  className="w-6 h-6 mr-2 rounded-full object-contain bg-white"
                                  onError={(e) => { e.target.onerror = null; e.target.src = "https://raw.githubusercontent.com/blacknodes/logos/main/default.png"; }}
                                />
                              )}
                              {validatorInfo ? validatorInfo.name : block.proposer.slice(0, 10) + '...'}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{block.txCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{block.timestamp}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {loading && <div className="text-center py-4">Loading...</div>}
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlockchainExplorerBlocksPage;